import React, { useState, useEffect } from 'react';
import { Sliders, Grid, List } from 'lucide-react';
import Layout from '../Layout';
import { getData } from '../../helpers/ApiSettings';
import ProductCard from '../ProductCard';

const ProductListingPage = () => {
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [priceRange, setPriceRange] = useState([0, 1000]);
    const [sortBy, setSortBy] = useState('name');
    const [viewMode, setViewMode] = useState('grid');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [isFilterVisible, setIsFilterVisible] = useState(false);

    useEffect(() => {
        fetchProducts();
        fetchCategories();
    }, [selectedCategories, priceRange, sortBy, currentPage]);

    const fetchProducts = async () => {
        try {
            // Build query parameters using URLSearchParams for proper encoding
            const params = new URLSearchParams();

            // Basic populate
            params.append('populate', '*');
            params.append('filters[available][$eq]', true);

            // Add category filters if any are selected
            if (selectedCategories.length > 0) {
                selectedCategories.forEach(catId => {
                    params.append('filters[category][id][$in]', catId);
                });
            }

            // Add pagination parameters (Strapi v5 format)
            params.append('pagination[page]', currentPage);
            params.append('pagination[pageSize]', '12');
            params.append('pagination[withCount]', 'true');

            // Build the full URL
            const queryString = params.toString();
            // console.log('API Query:', `/products?${queryString}`); // Debug log

            const response = await getData(`/products?${queryString}`);
            // console.log('API Response:', response); // Debug log

            setProducts(response.data.data);
            setTotalPages(response.data.meta.pagination.pageCount);
        } catch (error) {
            console.error('Error fetching products:', error);
            console.error('Error details:', error.response?.data);
            console.error('Error message:', error.response?.data?.error?.message);
            console.error('Error details:', error.response?.data?.error?.details);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await getData('/categories');
            setCategories(response.data.data);
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    const handleCategoryChange = (categoryId) => {
        setSelectedCategories(prev =>
            prev.includes(categoryId)
                ? prev.filter(id => id !== categoryId)
                : [...prev, categoryId]
        );
        setCurrentPage(1);
    };

    const handlePriceChange = (event, index) => {
        const newPriceRange = [...priceRange];
        newPriceRange[index] = Number(event.target.value);
        setPriceRange(newPriceRange);
        setCurrentPage(1);
    };

    const handleSortChange = (event) => {
        setSortBy(event.target.value);
        setCurrentPage(1);
    };

    const toggleFilterVisibility = () => {
        setIsFilterVisible(!isFilterVisible);
    };

    return (
        <Layout>
            <div className="container-fluid mt-4 mb-5">
                <div className="row">
                    {/* Filter Sidebar */}
                    <div className={`col-lg-3 mb-4 ${isFilterVisible ? 'd-block' : 'd-none d-lg-block'}`}>
                        <div className="card shadow-sm">
                            <div className="card-body">
                                <h5 className="card-title mb-4">Catégories</h5>
                                <div className="mb-4">
                                    {/* <h6 className="mb-3">Catégories</h6> */}
                                    {categories.map(category => (
                                        <div key={category.id} className="form-check mb-2">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id={`category-${category.id}`}
                                                checked={selectedCategories.includes(category.id)}
                                                onChange={() => handleCategoryChange(category.id)}
                                            />
                                            <label className="form-check-label" htmlFor={`category-${category.id}`}>
                                                {category.name}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Product Listing */}
                    <div className="col-lg-9">
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <button className="btn btn-outline-secondary d-lg-none" onClick={toggleFilterVisibility}>
                                <Sliders size={18} /> {isFilterVisible ? 'Masquer' : 'Afficher'} les filtres
                            </button>
                            <div className="d-flex align-items-center">
                                <div className="btn-group">
                                    <button
                                        className={`btn btn-outline-secondary ${viewMode === 'grid' ? 'active' : ''}`}
                                        onClick={() => setViewMode('grid')}
                                        title="Vue grille"
                                    >
                                        <Grid size={18} />
                                    </button>
                                    <button
                                        className={`btn btn-outline-secondary ${viewMode === 'list' ? 'active' : ''}`}
                                        onClick={() => setViewMode('list')}
                                        title="Vue liste"
                                    >
                                        <List size={18} />
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className={viewMode === 'grid' ? 'row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4' : ''}>
                            {products.map(product => (
                                <div
                                    key={product.id}
                                    className={viewMode === 'grid' ? 'col' : 'mb-4'}
                                >
                                    <ProductCard
                                        product={product}
                                        viewMode={viewMode}
                                    />
                                </div>
                            ))}
                        </div>

                        {/* Pagination */}
                        <nav className="mt-4">
                            <ul className="pagination justify-content-center">
                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                    <button className="page-link" onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}>Previous</button>
                                </li>
                                {[...Array(totalPages).keys()].map(page => (
                                    <li key={page} className={`page-item ${currentPage === page + 1 ? 'active' : ''}`}>
                                        <button className="page-link" onClick={() => setCurrentPage(page + 1)}>{page + 1}</button>
                                    </li>
                                ))}
                                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                    <button className="page-link" onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}>Next</button>
                                </li>
                            </ul>
                        </nav>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default ProductListingPage;